import * as React from 'react';
import { connect } from 'react-redux';

import navigate from 'libs/router/navigate';

import BaseInsightsPage from 'modules/insights/pages/BaseInsightsPage';

import InsightsReports from 'modules/insights/reports/components/reports/InsightsReports';
import InsightsReportsHeaderPage from 'modules/insights/reports/components/header/page/InsightsReportsHeaderPage';

import { danger } from 'redux-store/reducers/common-reducer';
import {
  createReport,
  deleteReport,
  duplicateReport,
  fetchReports,
  resetReports
} from 'modules/insights/reports/reducers/reports/collabary/insights-reports';

import { __t, applyTranslations } from 'utils/language';
import { getBrandProfileId } from 'utils/organization';
import {
  INSTAGRAM,
  REPORT
} from 'modules/insights/reports/utils/insights-reports';

import type { Organization } from 'types';
import type {
  GeneralProps,
  Report,
  ReportByType,
  ReportType
} from 'modules/insights/reports/types';

applyTranslations(require('modules/insights/reports/shared/insights-reports-shared.i18n.json'));

type StateProps = {
  organization: Organization,
  brandProfileId: number | undefined | null,
  reports: Array<Report>,
  isLoading: boolean
};

type DispatchProps = {
  dispatchFetchReports: (brandProfileId: number) => void,
  dispatchCreateReport: (report: Report) => Promise<Report>,
  dispatchDuplicateReport: (report: Report) => Promise<Report>,
  dispatchDeleteReport: (brandProfileId: number, reportId: number) => Promise<void>,
  dispatchResetReports: () => void,
  dispatchErrorPopup: (message: string) => void
};

type Props = StateProps & DispatchProps;

type State = {
  isActionEvent: boolean
};

const SHARED_MSG_KEY = 'insights_reports_shared';

class InsightsReportsPage extends React.PureComponent<Props, State> {
  state = {
    isActionEvent: false
  };

  componentDidMount() {
    const { brandProfileId, dispatchFetchReports } = this.props;

    if (brandProfileId) {
      dispatchFetchReports(brandProfileId);
    }
  }

  componentWillUnmount() {
    const { dispatchResetReports } = this.props;

    dispatchResetReports();
  }

  getReportsByType(): ReportByType {
    const { reports } = this.props;

    return {
      instagram: reports.filter(report => report.reportType === INSTAGRAM)
    };
  }

  getNewReportProps(props: GeneralProps): Report {
    const { brandProfileId } = this.props;

    return {
      id: -1,
      brandProfileId,
      reportType: props.reportType!,
      name: props.name,
      description: props.description,
      channelsInclude: false,
      countries: [],
      createdAt: '',
      updatedAt: '',
      startDate: '',
      endDate: ''
    };
  }

  getDuplicateReportProps(props: GeneralProps): Report {
    const { reports } = this.props;
    const selectedReport = (reports.find(report => report.id === props.reportId)) as Report;

    return {
      ...selectedReport,
      name: props.name,
      description: props.description
    };
  }

  handleActionEventToggle = (status: boolean): void => {
    this.setState({
      isActionEvent: status
    });
  };

  handleCreateReport = (props: GeneralProps): void => {
    const { brandProfileId, dispatchCreateReport } = this.props;

    if (brandProfileId) {
      dispatchCreateReport(this.getNewReportProps(props))
        .then(report => navigate.to(`/insights/reports/${String(props.reportType)}/${report.id}`))
        .catch(() => this.handleDispatchErrorPopup('creating', REPORT));
    }
  };

  handleDuplicateReport = (props: GeneralProps): void => {
    const { dispatchDuplicateReport } = this.props;
    const reportId = props.reportId;

    if (reportId) {
      this.handleActionEventToggle(true);

      dispatchDuplicateReport(this.getDuplicateReportProps(props))
        .then(() => this.handleActionEventToggle(false))
        .catch(() => this.handleDispatchErrorPopup('duplicating', REPORT));
    }
  };

  handleDeleteReport = ({
    reportId
  }: GeneralProps): void => {
    const { brandProfileId, dispatchDeleteReport } = this.props;

    if (brandProfileId && reportId) {
      this.handleActionEventToggle(true);

      dispatchDeleteReport(brandProfileId, reportId)
        .then(() => this.handleActionEventToggle(false))
        .catch(() => this.handleDispatchErrorPopup('deleting', REPORT));
    }
  };

  handleDispatchErrorPopup(actionType: string, itemType: string): void {
    const { dispatchErrorPopup } = this.props;

    this.handleActionEventToggle(false);

    dispatchErrorPopup(
      __t(`${SHARED_MSG_KEY}.messages.failure`, {
        actionType: __t(`${SHARED_MSG_KEY}.labels.${actionType}`),
        itemType: __t(`${SHARED_MSG_KEY}.labels.${itemType}`).toLowerCase()
      })
    );
  }

  renderReports(reportType: ReportType): React.ReactNode {
    const { organization, isLoading } = this.props;
    const { isActionEvent } = this.state;
    const reportsByType = this.getReportsByType();

    return (
      <div className="insights-reports-page__list">
        <InsightsReportsHeaderPage
          reportType={reportType}
        />
        <InsightsReports
          reportType={reportType}
          organization={organization}
          reports={reportsByType[reportType || INSTAGRAM]}
          isLoading={isLoading}
          isActionEvent={isActionEvent}
          isFreemium={organization?.isFreemium}
          onCreateReport={this.handleCreateReport}
          onDuplicateReport={this.handleDuplicateReport}
          onDeleteReport={this.handleDeleteReport}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const { organization } = this.props;

    return (
      <BaseInsightsPage
        documentTitle={__t(`${SHARED_MSG_KEY}.document_title`)}
        organization={organization}
      >
        <div className="insights-reports-page">
          {this.renderReports(INSTAGRAM)}
        </div>
      </BaseInsightsPage>
    );
  }
}

const mapStateToProps = ({ organizations, insightsReports }): StateProps => ({
  organization: organizations.currentOrganization,
  brandProfileId: getBrandProfileId(organizations.currentOrganization),
  reports: insightsReports.reports,
  isLoading: insightsReports.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchReports: (brandProfileId: number) => (
    dispatch(fetchReports(brandProfileId))
  ),
  dispatchCreateReport: (report: Report) => (
    dispatch(createReport(report))
  ),
  dispatchDuplicateReport: (report: Report) => (
    dispatch(duplicateReport(report))
  ),
  dispatchDeleteReport: (brandProfileId: number, reportId: number) => (
    dispatch(deleteReport(brandProfileId, reportId))
  ),
  dispatchResetReports: () => (
    dispatch(resetReports())
  ),
  dispatchErrorPopup: (message: string) => (
    dispatch(danger(message))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(InsightsReportsPage);
